/*!
 * Copyright 2017 - 2020 by ChartIQ, Inc.
 * All rights reserved.
 */

import { Logger } from "../clients/logger";
import { System } from "./system";
import SystemManagerClient from "./systemManagerClient";
import { singleton } from "./Singleton";

/**
 * The controller keeps track of the number of times that particular
 * notifications have been sent
 */
class ToastController {
	alertOnceSinceStartUp = {};

	alertCurrentCount = {};
}

export type ToastFrequency = "ALWAYS" | "ONCE-SINCE-STARTUP" | "MAX-COUNT";

export type ToastOptions = {
	maxCount?: number;
	duration?: number;
};

/**
 * Conditionally alerts the end user using a desktop notification (toast).
 *
 * In addition to the params specified here, params may also include any valid parameter for electron.Notification.
 *
 * Set topic = "devModeOnly" so that UserNotifications only are displayed when running from the seed.
 *
 * @param topic Specifies a category for the notification. This parameter is reserved for future use; it is designed to filter notifications (e.g., applying regEx's defined in config to determine which notifications are displayed). Any topic string can be specified; however "system" is the recommended topic for system notifications applicable both to end uses and to developers. "dev" is the recommended topic for notifications applicable only during development (e.g., a notification that <i>config.json</i> has an illegal value).
 * @param frequency Either "ALWAYS", "ONCE-SINCE-STARTUP", or "MAX-COUNT" to determine if alert should be displayed. Note, the frequencies are based on the number of notifications emitted from a window (as opposed to system wide).
 * @param identifier Uniquely identifies this specific notification message. Used when "frequency" is set to "ONCE-SINCE-STARTUP" or "MAX-COUNT".
 * @param message Message to display in the notification. Typically a string. Finsemble's built in templating accepts and object. See <i>../src/components/notification/notification.html<i>.
 * @param params
 * @param params.maxCount Specifies the max number of notifications to display for specified identifier when frequency="MAX-COUNT" (default is 1).
 * @param params.duration Time in milliseconds before auto-dismissing the notification (defaults to 24 hours).
 *
 * @example
 * FSBL.UserNotification.alert("system", "ONCE-SINCE-STARTUP", "MANIFEST-Error", message);
 * FSBL.UserNotification.alert("dev", "ALWAYS", "Config-Error", message, { duration: 1000 * 5 });
 * FSBL.UserNotification.alert("dev", "MAX-COUNT", "Transport-Failure", message, { maxCount: 2 });
 */

export const toast = (
	topic: string,
	frequency: ToastFrequency,
	identifier: string,
	message: any,
	params?: ToastOptions
) => {
	const controller = singleton("Toast", ToastController);
	params = params || {};
	let alertUser = false;
	const key = `toast.${identifier}`;
	const duration = params.duration || 1000 * 60 * 60 * 24;

	switch (frequency) {
		case "ONCE-SINCE-STARTUP": {
			if (key in controller.alertOnceSinceStartUp) {
				alertUser = false;
			} else {
				// if no key then must be first time
				alertUser = true;
				controller.alertOnceSinceStartUp[key] = true;
			}
			break;
		}
		case "MAX-COUNT": {
			let currentCount = 0;
			const maxCount = params.maxCount || 1;
			if (key in controller.alertCurrentCount) {
				currentCount = controller.alertCurrentCount[key];
			}
			controller.alertCurrentCount[key] = ++currentCount; // increment and store
			if (currentCount <= maxCount) {
				alertUser = true;
			}
			break;
		}
		default: {
			// default to "ALWAYS"
			alertUser = true;
		}
	}

	Logger.log("toast", topic, alertUser, frequency, identifier, message, params);
	if (alertUser) {
		const notifyObject: Record<string, any> = {
			message,
			timeout: duration,
			// Pick up any optional params to be passed to electron.Notification
			...params,
			devModeOnly: topic === "devModeOnly",
		};
		if (!notifyObject.title && topic === "devModeOnly") {
			notifyObject.title = "Finsemble Developer Notification";
		}
		SystemManagerClient.systemLog({ notification: true }, `Notification: ${message}`);
		System.Notification.send(notifyObject);
	}
};

export default {
	toast,
	alert: toast,
};
